// src/contexts/LocationContext.jsx
import React, { createContext, useState, useEffect } from 'react';
import { getStorageItem, setStorageItem } from '../utils/storage';
import ApiService from '../services/ApiService';

export const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationSkipped, setLocationSkipped] = useState(false);
  const [distributorId, setDistributorId] = useState(null);
  const [nearestDistributor, setNearestDistributor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backgroundUpdate, setBackgroundUpdate] = useState(false);
  
  // Nouveaux états pour la gestion du lieu de livraison
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  
  // ✅ NOUVEAU : Callback pour notifier les changements de distributeur
  const [distributorChangeCallback, setDistributorChangeCallback] = useState(null);

  // ✅ NOUVEAU v4.0 : État "hors zone" pour le système Point-in-Polygon
  const [outOfZone, setOutOfZone] = useState(false);
  const [outOfZoneMessage, setOutOfZoneMessage] = useState('');

  // Charger immédiatement le distributeur depuis le cache, puis vérifier la position en arrière-plan
  useEffect(() => {
    const init = async () => {
      try {
        // console.log("🚀 Initialisation du LocationContext...");
        
        // Charger le lieu de livraison sauvegardé
        const savedDeliveryLocation = getStorageItem('deliveryLocation');
        const savedDeliveryAddress = getStorageItem('deliveryAddress');
        
        if (savedDeliveryLocation && savedDeliveryAddress) {
          try {
            const parsedLocation = JSON.parse(savedDeliveryLocation);
            setDeliveryLocation(parsedLocation);
            setDeliveryAddress(savedDeliveryAddress);
            // console.log("📍 Lieu de livraison chargé depuis le cache:", parsedLocation);
          } catch (parseError) {
            // console.warn("Erreur lors du parsing du lieu de livraison:", parseError);
          }
        }
		
		const savedHistory = getStorageItem('deliveryHistory');
		if (savedHistory) {
		  try {
			const parsedHistory = JSON.parse(savedHistory);
			setDeliveryHistory(parsedHistory);
			// console.log("📍 Historique des lieux chargé:", parsedHistory.length, "lieux");
		  } catch (parseError) {
			// console.warn("Erreur lors du parsing de l'historique:", parseError);
		  }
		}
        
        // ÉTAPE 1: Charger immédiatement le distributeur depuis le cache
        const cachedDistributorId = getStorageItem('distributorId');
        const cachedDistributor = getStorageItem('nearestDistributor');
        const cachedOutOfZone = getStorageItem('outOfZone');
        
        // ✅ v4.0 : Si le cache indique "hors zone", restaurer cet état
        if (cachedOutOfZone === 'true') {
          setOutOfZone(true);
          setOutOfZoneMessage(getStorageItem('outOfZoneMessage') || '');
          setLoading(false);
          
          // Vérifier en arrière-plan si la zone a changé
          setTimeout(() => {
            verifyLocationInBackground();
          }, 100);
          
          return;
        }
        
        if (cachedDistributorId && cachedDistributor) {
          try {
            const parsedDistributor = JSON.parse(cachedDistributor);
            // console.log("✅ Distributeur chargé depuis le cache:", parsedDistributor);
            
            setDistributorId(parseInt(cachedDistributorId));
            setNearestDistributor(parsedDistributor);
            setOutOfZone(false);
            
            // LE DISTRIBUTEUR EST MAINTENANT DISPONIBLE IMMÉDIATEMENT
            setLoading(false);
            
            // ÉTAPE 2: Vérifier en arrière-plan si l'utilisateur a changé de position
            setTimeout(() => {
              verifyLocationInBackground();
            }, 100); // Petit délai pour laisser le composant se monter
            
            return; // Sortir ici, l'initialisation principale est terminée
          } catch (parseError) {
            // console.warn("Erreur lors du parsing du distributeur en cache:", parseError);
          }
        }
        
        // ✅ v4.0 : Pas de cache — on ne peut plus assigner de distributeur par défaut
        // Il faut attendre la position GPS ou la sélection d'un lieu de livraison
        // pour déterminer la zone via Point-in-Polygon
        // console.log("🔄 Aucun distributeur en cache, attente de la position GPS...");
        
        // Vérifier si on a une position sauvegardée pour déterminer la zone
        const savedLocation = getStorageItem('userLocation');
        if (savedLocation) {
          try {
            const parsedLocation = JSON.parse(savedLocation);
            setUserLocation(parsedLocation);
            
            // Déterminer la zone à partir de la position sauvegardée
            await findDistributorByZone(parsedLocation.latitude, parsedLocation.longitude);
            return;
          } catch (parseError) {
            // console.warn("Erreur lors du parsing de la position:", parseError);
          }
        }
        
        // Aucune position sauvegardée — l'utilisateur devra activer la géolocalisation
        // ou sélectionner un lieu de livraison
        setLoading(false);
        
      } catch (error) {
        // console.error('❌ Erreur lors de l\'initialisation de la localisation:', error);
        setError('Erreur lors de l\'initialisation de la localisation');
        setLoading(false);
      }
    };

    init();
  }, []);

  // ============================================================
  // ✅ v4.0 : NOUVELLE FONCTION — Recherche du distributeur par zone (Point-in-Polygon)
  // Remplace l'ancien système Haversine
  // ============================================================
  const findDistributorByZone = async (latitude, longitude, isBackgroundUpdate = false) => {
    try {
      // console.log(`🔍 Vérification de zone pour: ${latitude}, ${longitude}`);
      
      if (!isBackgroundUpdate) {
        setLoading(true);
      }
      
      // Appeler l'API Point-in-Polygon
      const response = await ApiService.checkZone(latitude, longitude);
      
      if (!response || !response.success) {
        throw new Error(response?.message || 'Erreur lors de la vérification de zone');
      }
      
      if (response.in_zone && response.distributeur) {
        // ✅ Le client est dans une zone couverte
        const distributor = {
          id: response.distributeur.id,
          nom: response.distributeur.nom,
          adresse: response.distributeur.adresse,
          telephone: response.distributeur.telephone,
          email: response.distributeur.email,
          zone: response.zone,
          loadedFrom: 'zone-api'
        };
        
        // Sauvegarder l'ancien ID avant de changer
        const oldDistributorId = distributorId;
        
        setNearestDistributor(distributor);
        setDistributorId(distributor.id);
        setOutOfZone(false);
        setOutOfZoneMessage('');
        setError(null);
        
        // Sauvegarder dans le cache
        setStorageItem('distributorId', distributor.id.toString());
        setStorageItem('nearestDistributor', JSON.stringify(distributor));
        setStorageItem('outOfZone', 'false');
        setStorageItem('outOfZoneMessage', '');
        
        // Notifier le changement de distributeur si nécessaire
        if (oldDistributorId && oldDistributorId !== distributor.id && distributorChangeCallback) {
          // console.log("🛒 Notification du changement de distributeur");
          distributorChangeCallback(oldDistributorId, distributor.id);
        }
        
        if (!isBackgroundUpdate) {
          setLoading(false);
        }
        
        return distributor;
        
      } else {
        // ❌ Le client est hors zone
        const message = response.message || 'Votre position ne se trouve dans aucune zone de livraison pour le moment. Nous vous informerons dès que votre zone sera couverte.';
        
        setOutOfZone(true);
        setOutOfZoneMessage(message);
        setNearestDistributor(null);
        setDistributorId(null);
        
        // Sauvegarder dans le cache
        setStorageItem('outOfZone', 'true');
        setStorageItem('outOfZoneMessage', message);
        setStorageItem('distributorId', '');
        setStorageItem('nearestDistributor', '');
        
        if (!isBackgroundUpdate) {
          setLoading(false);
        }
        
        return null;
      }
      
    } catch (error) {
      // console.error('Erreur lors de la vérification de zone:', error);
      
      if (!isBackgroundUpdate) {
        setError('Impossible de vérifier votre zone de livraison');
        setLoading(false);
      }
      
      // En cas d'erreur réseau, essayer le cache
      const cached = getStorageItem('nearestDistributor');
      if (cached && !nearestDistributor) {
        try {
          const distributor = JSON.parse(cached);
          setNearestDistributor(distributor);
          setDistributorId(distributor.id);
          return distributor;
        } catch (parseError) {
          // console.error("Erreur lors du parsing du distributeur en cache:", parseError);
        }
      }
      
      return null;
    }
  };

  // ============================================================
  // ANCIEN SYSTÈME HAVERSINE — Commenté, remplacé par findDistributorByZone
  // ============================================================
  // const getDefaultDistributorQuickly = async () => { ... };
  // const findNearestDistributor_HAVERSINE = async (latitude, longitude, isBackgroundUpdate = false) => { ... };

  // ✅ v4.0 : findNearestDistributor pointe désormais vers le système par zones
  // Le nom est conservé pour compatibilité avec les autres composants qui l'appellent
  const findNearestDistributor = async (latitude, longitude, isBackgroundUpdate = false) => {
    return findDistributorByZone(latitude, longitude, isBackgroundUpdate);
  };

  // Vérification en arrière-plan si l'utilisateur a changé de position
  const verifyLocationInBackground = async () => {
    // console.log("🔍 Vérification de la position en arrière-plan...");
    setBackgroundUpdate(true);
    
    try {
      // Vérifier si l'utilisateur a ignoré la localisation
      const skipped = getStorageItem('locationSkipped');
      if (skipped === 'true') {
        // console.log("📍 Localisation ignorée par l'utilisateur");
        setLocationSkipped(true);
        setBackgroundUpdate(false);
        return;
      }

      // Récupérer la position sauvegardée
      const savedLocation = getStorageItem('userLocation');
      if (savedLocation) {
        try {
          const parsedLocation = JSON.parse(savedLocation);
          const locationAge = Date.now() - parsedLocation.timestamp;
          
          // Si la position est récente (moins de 1 heure), ne pas la revérifier
          if (locationAge < 3600000) { // 1 heure
            // console.log("📍 Position récente, pas de révérification nécessaire");
            setUserLocation(parsedLocation);
            setBackgroundUpdate(false);
            return;
          }
          
          // console.log("📍 Position ancienne, tentative de mise à jour...");
          setUserLocation(parsedLocation);
          
          // Essayer d'obtenir une position plus récente (sans bloquer l'UI)
          await tryUpdateLocationInBackground();
          
        } catch (parseError) {
          // console.error('Erreur lors du parsing de la position:', parseError);
        }
      } else {
        // console.log("📍 Aucune position sauvegardée");
      }
      
    } catch (error) {
      // console.error('Erreur lors de la vérification de la position:', error);
    } finally {
      setBackgroundUpdate(false);
    }
  };

  // Tentative de mise à jour de la position en arrière-plan
  const tryUpdateLocationInBackground = async () => {
    if (!navigator.geolocation) {
      // console.log("📍 Géolocalisation non supportée");
      return;
    }

    try {
      // console.log("📍 Tentative d'obtention d'une nouvelle position...");
      
      const position = await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Timeout de géolocalisation'));
        }, 10000); // 10 secondes max
        
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            clearTimeout(timeoutId);
            resolve(pos);
          },
          (err) => {
            clearTimeout(timeoutId);
            reject(err);
          },
          {
            enableHighAccuracy: false, // Plus rapide
            timeout: 8000,
            maximumAge: 300000 // Accepter une position de moins de 5 minutes
          }
        );
      });

      const newLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: Date.now()
      };

      // console.log("📍 Nouvelle position obtenue:", newLocation);

      // Vérifier si l'utilisateur a bougé significativement
      const oldLocation = JSON.parse(getStorageItem('userLocation'));
      const distance = calculateDistance(
        oldLocation.latitude,
        oldLocation.longitude,
        newLocation.latitude,
        newLocation.longitude
      );

      // console.log(`📍 Distance depuis la dernière position: ${distance.toFixed(2)} km`);

      // Si l'utilisateur a bougé de plus de 1 km, revérifier la zone
      if (distance > 1) {
        // console.log("📍 Utilisateur a bougé, revérification de la zone...");
        setUserLocation(newLocation);
        setStorageItem('userLocation', JSON.stringify(newLocation));
        
        // ✅ v4.0 : Revérifier la zone au lieu du Haversine
        await findDistributorByZone(newLocation.latitude, newLocation.longitude, true);
      } else {
        // console.log("📍 Utilisateur n'a pas bougé significativement");
        // Mettre à jour juste le timestamp
        setUserLocation(newLocation);
        setStorageItem('userLocation', JSON.stringify(newLocation));
      }

    } catch (error) {
      // console.log("📍 Impossible d'obtenir une nouvelle position:", error.message);
      // Ce n'est pas grave, on continue avec l'ancienne position
    }
  };

  // ✅ v4.0 : findBestDistributorInBackground utilise désormais les zones
  const findBestDistributorInBackground = async () => {
    // console.log("🔍 Recherche du meilleur distributeur en arrière-plan...");
    setBackgroundUpdate(true);
    
    try {
      const savedLocation = getStorageItem('userLocation');
      if (savedLocation) {
        const parsedLocation = JSON.parse(savedLocation);
        await findDistributorByZone(parsedLocation.latitude, parsedLocation.longitude, true);
      } else {
        // console.log("📍 Aucune position disponible pour déterminer la zone");
      }
    } catch (error) {
      // console.error('Erreur lors de la recherche du distributeur:', error);
    } finally {
      setBackgroundUpdate(false);
    }
  };

  // Activer la géolocalisation (fonction améliorée)
  const enableGeolocation = () => {
    return new Promise(async (resolve, reject) => {
      if (!navigator.geolocation) {
        const errorMessage = 'La géolocalisation n\'est pas supportée par votre navigateur';
        setError(errorMessage);
        reject(new Error(errorMessage));
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 30000
          });
        });
        
        const userLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: Date.now()
        };
        
        // console.log("📍 Position obtenue:", userLocation);
        
        // Sauvegarder la position
        setUserLocation(userLocation);
        setStorageItem('userLocation', JSON.stringify(userLocation));
        
        // ✅ v4.0 : Vérifier la zone au lieu du Haversine
        const distributor = await findDistributorByZone(userLocation.latitude, userLocation.longitude);
        setLoading(false);
        resolve(distributor);
        
      } catch (error) {
        // console.error('Erreur de géolocalisation:', error);
        
        let errorMessage = 'Erreur lors de l\'obtention de votre position';
        
        if (error.code === 1) {
          errorMessage = 'Vous avez refusé l\'accès à votre position';
        } else if (error.code === 2) {
          errorMessage = 'Position non disponible. Vérifiez que le GPS est activé';
        } else if (error.code === 3) {
          errorMessage = 'La demande de position a expiré';
        }
        
        setError(errorMessage);
        setLoading(false);
        reject(new Error(errorMessage));
      }
    });
  };

  // Ignorer la localisation
  const skipGeolocation = async () => {
    try {
      setLocationSkipped(true);
      setStorageItem('locationSkipped', 'true');
      
      // ✅ v4.0 : Sans position GPS, on ne peut pas déterminer la zone
      // L'utilisateur devra sélectionner un lieu de livraison
      // Si on a un distributeur en cache, on le garde
      if (!nearestDistributor) {
        // Pas de distributeur en cache — on ne peut rien faire
        // L'utilisateur verra le sélecteur de lieu de livraison
        // console.log("📍 Localisation ignorée, aucun distributeur assigné");
      }
      
      return nearestDistributor;
    } catch (error) {
      // console.error('Erreur lors de l\'ignorance de la géolocalisation:', error);
      setError('Impossible de charger le distributeur par défaut');
      return null;
    }
  };

	// Nouvelle fonction pour définir le lieu de livraison
	const setDeliveryLocationData = async (placeDetails, address) => {
	  try {
		// console.log("📍 Définition du lieu de livraison:", placeDetails);
		
		const location = {
		  latitude: placeDetails.latitude,
		  longitude: placeDetails.longitude,
		  address: address,
		  name: placeDetails.name,
		  timestamp: Date.now()
		};
		
		setDeliveryLocation(location);
		setDeliveryAddress(address);
		
		// Sauvegarder dans le localStorage
		setStorageItem('deliveryLocation', JSON.stringify(location));
		setStorageItem('deliveryAddress', address);
		
		// NOUVEAU : Ajouter à l'historique
		addToDeliveryHistory(location, address);
		
		// ✅ v4.0 : Vérifier la zone pour ce lieu de livraison
		const distributor = await findDistributorByZone(location.latitude, location.longitude);
		
		return distributor;
	  } catch (error) {
		// console.error('Erreur lors de la définition du lieu de livraison:', error);
		throw error;
	  }
	};

  // Ajouter un lieu à l'historique
	const addToDeliveryHistory = (location, address) => {
	  try {
		const newHistoryItem = {
		  id: Date.now(), // ID unique basé sur le timestamp
		  latitude: location.latitude,
		  longitude: location.longitude,
		  address: address,
		  name: location.name || address.split(',')[0], // Nom ou première partie de l'adresse
		  timestamp: Date.now(),
		  useCount: 1 // Compteur d'utilisation pour trier par popularité
		};

		// Récupérer l'historique actuel
		let updatedHistory = [...deliveryHistory];
		
		// Vérifier si ce lieu existe déjà (par coordonnées ou adresse similaire)
		const existingIndex = updatedHistory.findIndex(item => 
		  (Math.abs(item.latitude - location.latitude) < 0.0001 && 
		   Math.abs(item.longitude - location.longitude) < 0.0001) ||
		  item.address.toLowerCase() === address.toLowerCase()
		);

		if (existingIndex !== -1) {
		  // Si le lieu existe, augmenter son compteur d'utilisation et le remonter
		  updatedHistory[existingIndex].useCount += 1;
		  updatedHistory[existingIndex].timestamp = Date.now();
		  
		  // Réorganiser pour mettre les plus utilisés en premier
		  updatedHistory.sort((a, b) => b.useCount - a.useCount);
		} else {
		  // Ajouter le nouveau lieu au début
		  updatedHistory.unshift(newHistoryItem);
		  
		  // Limiter l'historique à 5 lieux maximum
		  if (updatedHistory.length > 5) {
			updatedHistory = updatedHistory.slice(0, 5);
		  }
		}

		// Sauvegarder
		setDeliveryHistory(updatedHistory);
		setStorageItem('deliveryHistory', JSON.stringify(updatedHistory));
		
		// console.log("📍 Lieu ajouté à l'historique:", newHistoryItem.name);
	  } catch (error) {
		// console.error("Erreur lors de l'ajout à l'historique:", error);
	  }
	};

	// Supprimer un lieu de l'historique
	const removeFromDeliveryHistory = (locationId) => {
	  try {
		const updatedHistory = deliveryHistory.filter(item => item.id !== locationId);
		setDeliveryHistory(updatedHistory);
		setStorageItem('deliveryHistory', JSON.stringify(updatedHistory));
		
		// console.log("🗑️ Lieu supprimé de l'historique");
	  } catch (error) {
		// console.error("Erreur lors de la suppression de l'historique:", error);
	  }
	};

	// Sélectionner un lieu depuis l'historique
	const selectFromDeliveryHistory = async (historyItem) => {
	  try {
		// console.log("📍 Sélection depuis l'historique:", historyItem);
		
		// Créer un objet location compatible
		const location = {
		  latitude: historyItem.latitude,
		  longitude: historyItem.longitude,
		  address: historyItem.address,
		  name: historyItem.name,
		  timestamp: Date.now()
		};
		
		// Mettre à jour le lieu de livraison actuel
		setDeliveryLocation(location);
		setDeliveryAddress(historyItem.address);
		
		// Sauvegarder dans le localStorage
		setStorageItem('deliveryLocation', JSON.stringify(location));
		setStorageItem('deliveryAddress', historyItem.address);
		
		// Augmenter le compteur d'utilisation
		addToDeliveryHistory(location, historyItem.address);
		
		// ✅ v4.0 : Vérifier la zone pour ce lieu
		const distributor = await findDistributorByZone(location.latitude, location.longitude);
		
		return distributor;
	  } catch (error) {
		// console.error('Erreur lors de la sélection depuis l\'historique:', error);
		throw error;
	  }
	};
	
  // Réinitialiser le lieu de livraison
  const clearDeliveryLocation = () => {
    setDeliveryLocation(null);
    setDeliveryAddress('');
    localStorage.removeItem('deliveryLocation');
    localStorage.removeItem('deliveryAddress');
  };

  // Calculer la distance entre deux points géographiques (formule de Haversine)
  // ✅ v4.0 : Conservée pour d'éventuels besoins d'affichage de distance
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (typeof lat1 !== 'number' || typeof lon1 !== 'number' || 
        typeof lat2 !== 'number' || typeof lon2 !== 'number') {
      // console.warn("Coordonnées invalides pour le calcul de distance:", {lat1, lon1, lat2, lon2});
      return Infinity;
    }
    
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return distance;
  };

  return (
    <LocationContext.Provider
      value={{
        userLocation,
        locationSkipped,
        distributorId,
        nearestDistributor,
        loading,
        error,
        backgroundUpdate,
        deliveryLocation,
        deliveryAddress,
		deliveryHistory, // NOUVEAU
        enableGeolocation,
        skipGeolocation,
        findNearestDistributor,
        calculateDistance,
        setDeliveryLocationData,
        clearDeliveryLocation,
		addToDeliveryHistory, // NOUVEAU
		removeFromDeliveryHistory, // NOUVEAU
		selectFromDeliveryHistory, // NOUVEAU
		setDistributorChangeCallback, // ✅ NOUVEAU : Ajout du setter dans le contexte
		outOfZone, // ✅ v4.0 : État hors zone
		outOfZoneMessage // ✅ v4.0 : Message hors zone
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export default LocationProvider;